import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  LogOut, Calendar, Clock, Plus, CheckCircle, XCircle,
  Bell, BarChart, ChevronLeft, ChevronRight, Fingerprint
} from 'lucide-react';

import { ManagerShiftsTab } from '@/components/manager/ManagerShiftsTab';
import { ManagerRequestsTab } from '@/components/manager/ManagerRequestsTab';
import { ManagerReportsTab } from '@/components/manager/ManagerReportsTab';
import { ManagerNotificationsTab } from '@/components/manager/ManagerNotificationsTab';
import { ManagerUnavailabilityTab } from '@/components/manager/ManagerUnavailabilityTab';
import {
  CreateShiftDialog, AssignShiftDialog, EditShiftDialog,
  UnavailabilityDialog, DirectCreateShiftDialog
} from '@/components/manager/ManagerDialogs';
import { ApplyTemplateWeekDialog } from '@/components/admin/AdminDialogs';
import { AttendanceView } from '@/components/shared/AttendanceView';

export default function ManagerDashboard() {
  const { user, logout } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('shifts');
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [shiftRequests, setShiftRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unavailability, setUnavailability] = useState([]);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [showCreateShift, setShowCreateShift] = useState(false);
  const [showEditShift, setShowEditShift] = useState(false);
  const [showAssignShift, setShowAssignShift] = useState(false);
  const [showUnavailability, setShowUnavailability] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [assigningShift, setAssigningShift] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [branchStaff, setBranchStaff] = useState([]);
  const [overrideHoursLimit, setOverrideHoursLimit] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(getMonday(new Date()));
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [managerOnDuty, setManagerOnDuty] = useState(null);
  const [systemRoles, setSystemRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(user.branch);
  const [showCreateDirectShift, setShowCreateDirectShift] = useState(false);
  const [directShiftForm, setDirectShiftForm] = useState({
    shift_code: '', start_time: '07:00', end_time: '15:00',
    lunch_minutes: 30, max_capacity: 1, dates: []
  });
  const [shiftForm, setShiftForm] = useState({ template_id: '', dates: [] });
  const [templateWeeks, setTemplateWeeks] = useState([]);
  const [showApplyTemplateWeek, setShowApplyTemplateWeek] = useState(false);

  const isOwnBranch = selectedBranch === user.branch;

  useEffect(() => {
    fetchShiftTemplates();
    fetchShifts();
    fetchShiftRequests();
    fetchNotifications();
    fetchWeeklyReport();
    fetchBranchStaff();
    fetchManagerOnDuty();
    fetchSystemRoles();
    fetchBranches();
    fetchTemplateWeeks();
    if (activeTab === 'unavailability' || activeTab === 'shifts') {
      fetchUnavailability();
    }
  }, [selectedWeek, activeTab, selectedBranch]);

  // ─── Data Fetchers ───
  const fetchBranches = async () => {
    try { const r = await axios.get(`${API}/branches`); setBranches(r.data); }
    catch { console.error('Failed to fetch branches'); }
  };
  const fetchSystemRoles = async () => {
    try { const r = await axios.get(`${API}/system-roles`); setSystemRoles(r.data); }
    catch { console.error('Failed to fetch system roles'); }
  };
  const fetchManagerOnDuty = async () => {
    try { const r = await axios.get(`${API}/manager-on-duty`, { params: { week_start: selectedWeek } }); setManagerOnDuty(r.data); }
    catch { console.error('Failed to fetch manager on duty'); }
  };
  const fetchBranchStaff = async () => {
    try { const r = await axios.get(`${API}/manager/branch-staff`); setBranchStaff(r.data); }
    catch { console.error('Failed to fetch branch staff'); }
  };
  const fetchShiftTemplates = async () => {
    try { const r = await axios.get(`${API}/shift-templates`); setShiftTemplates(r.data); }
    catch { toast.error('Failed to fetch shift templates'); }
  };
  const fetchShifts = async () => {
    try {
      const weekEnd = new Date(selectedWeek);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const r = await axios.get(`${API}/shifts`, {
        params: { date_from: selectedWeek, date_to: weekEnd.toISOString().split('T')[0], branch: selectedBranch }
      });
      // Normalize legacy shifts: ensure assigned_users array is populated
      const normalized = r.data.map(s => {
        if ((!s.assigned_users || s.assigned_users.length === 0) && s.assigned_user_id) {
          return { ...s, assigned_users: [{ user_id: s.assigned_user_id, username: s.assigned_username || '' }] };
        }
        return s;
      });
      setShifts(normalized);
    } catch { toast.error('Failed to fetch shifts'); }
  };
  const fetchShiftRequests = async () => {
    try { const r = await axios.get(`${API}/shift-requests`, { params: { status_filter: 'pending', branch: selectedBranch } }); setShiftRequests(r.data); }
    catch { toast.error('Failed to fetch shift requests'); }
  };
  const fetchNotifications = async () => {
    try { const r = await axios.get(`${API}/notifications`); setNotifications(r.data); }
    catch { console.error('Failed to fetch notifications'); }
  };
  const fetchWeeklyReport = async () => {
    try { const r = await axios.get(`${API}/reports/weekly-hours`, { params: { week_start: selectedWeek, branch: selectedBranch } }); setWeeklyReport(r.data); }
    catch { console.error('Failed to fetch weekly report'); }
  };
  const fetchUnavailability = async () => {
    try {
      const weekDates = getWeekDates();
      const r = await axios.get(`${API}/unavailability`, { params: { date_from: weekDates[0], date_to: weekDates[6], branch: selectedBranch } });
      setUnavailability(r.data);
    } catch { console.error('Failed to fetch unavailability'); }
  };
  const fetchTemplateWeeks = async () => {
    try { const r = await axios.get(`${API}/template-weeks`); setTemplateWeeks(r.data); }
    catch { console.error('Failed to fetch template weeks'); }
  };
  const handleApplyTemplateWeek = async (twId) => {
    const existing = shifts.length;
    if (existing > 0 && !confirm(`This week already has ${existing} shift(s). The template shifts will be added alongside existing ones. Continue?`)) return;
    try {
      const r = await axios.post(`${API}/template-weeks/${twId}/apply`, null, { params: { week_start: selectedWeek } });
      toast.success(r.data.message); setShowApplyTemplateWeek(false); fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to apply template week'); }
  };

  // ─── Handlers ───
  const handleCreateShifts = async (e) => {
    e.preventDefault();
    try {
      for (const date of shiftForm.dates) {
        await axios.post(`${API}/shifts`, { template_id: shiftForm.template_id, date });
      }
      toast.success(`Created ${shiftForm.dates.length} shifts`);
      setShowCreateShift(false);
      setShiftForm({ template_id: '', dates: [] });
      fetchShifts();
    } catch { toast.error('Failed to create shifts'); }
  };

  const handleCreateDirectShifts = async (e) => {
    e.preventDefault();
    if (!directShiftForm.shift_code) { toast.error('Please select a shift code'); return; }
    if (directShiftForm.dates.length === 0) { toast.error('Please select at least one date'); return; }
    try {
      await axios.post(`${API}/shifts/direct`, {
        shift_code: directShiftForm.shift_code, start_time: directShiftForm.start_time,
        end_time: directShiftForm.end_time, lunch_minutes: parseInt(directShiftForm.lunch_minutes) || 0,
        max_capacity: parseInt(directShiftForm.max_capacity) || 1, dates: directShiftForm.dates
      });
      toast.success(`Created ${directShiftForm.dates.length} shift(s)`);
      setShowCreateDirectShift(false);
      setDirectShiftForm({ shift_code: '', start_time: '07:00', end_time: '15:00', lunch_minutes: 30, max_capacity: 1, dates: [] });
      fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create shifts'); }
  };

  const handleShiftRequest = async (requestId, action) => {
    try {
      await axios.post(`${API}/shift-requests/action`, { request_id: requestId, action });
      toast.success(`Request ${action}d`);
      fetchShiftRequests(); fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to process request'); }
  };

  const handleExportShiftsPDF = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ week_start: selectedWeek, token });
    window.open(`${API}/reports/shifts/pdf?${params.toString()}`, '_blank');
  };

  const handleEditShift = (shift) => { setEditingShift(shift); setShowEditShift(true); };

  const handleSaveShiftEdit = async () => {
    try {
      await axios.put(`${API}/shifts/${editingShift.id}`, {
        date: editingShift.date, start_time: editingShift.start_time, end_time: editingShift.end_time,
        shift_code: editingShift.shift_code, lunch_minutes: editingShift.lunch_minutes, max_capacity: editingShift.max_capacity
      });
      toast.success('Shift updated successfully');
      setShowEditShift(false); setEditingShift(null); fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update shift'); }
  };

  const handleUnassignShift = async (shiftId) => {
    if (!window.confirm('Remove this staff member from the shift?')) return;
    try {
      await axios.post(`${API}/shifts/${shiftId}/unassign`);
      toast.success('Staff member removed from shift');
      setShowEditShift(false); setEditingShift(null); fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to unassign shift'); }
  };
  const handleUnassignUser = async (shiftId, userId) => {
    try { await axios.post(`${API}/shifts/${shiftId}/unassign?user_id=${userId}`); toast.success('Staff member removed'); fetchShifts(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to remove staff member'); }
  };

  const openAssignDialog = async (shift) => {
    setAssigningShift(shift); setSelectedStaffIds([]);
    try {
      const r = await axios.get(`${API}/users/available-for-shift`, {
        params: { date: shift.date, start_time: shift.start_time, end_time: shift.end_time, branch: shift.branch, shift_id: shift.id }
      });
      const eligible = r.data.filter(u => u.role === 'staff' && u.branches.includes(shift.branch) && u.allowed_roles.includes(shift.shift_code));
      setStaffList(eligible);
      setShowAssignShift(true);
      if (eligible.length === 0) toast.warning('No available staff for this shift time.');
    } catch { toast.error('Failed to fetch available staff'); }
  };

  const handleAssignShiftMultiple = async () => {
    if (selectedStaffIds.length === 0) { toast.error('Please select at least one staff member'); return; }
    try {
      const r = await axios.post(`${API}/shifts/${assigningShift.id}/assign-multiple`, { user_ids: selectedStaffIds, override_hours_limit: overrideHoursLimit });
      toast.success(r.data.message);
      if (r.data.errors?.length > 0) r.data.errors.forEach(err => toast.error(err));
      setShowAssignShift(false); setAssigningShift(null); setOverrideHoursLimit(false); setSelectedStaffIds([]); fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to assign shift'); }
  };

  const toggleStaffSelection = (staffId) => {
    setSelectedStaffIds(prev => prev.includes(staffId) ? prev.filter(id => id !== staffId) : [...prev, staffId]);
  };

  const handleAddUnavailability = async (data) => {
    try {
      await axios.post(`${API}/unavailability`, data);
      toast.success('Unavailability added'); fetchUnavailability(); setShowUnavailability(false);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add unavailability'); }
  };

  const handleDeleteUnavailability = async (id) => {
    if (!window.confirm('Remove this unavailability period?')) return;
    try {
      await axios.delete(`${API}/unavailability/${id}`);
      toast.success('Unavailability removed'); fetchUnavailability();
    } catch { toast.error('Failed to remove unavailability'); }
  };

  // ─── Utilities ───
  function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff)).toISOString().split('T')[0];
  }

  const getWeekDates = () => {
    const dates = [];
    const start = new Date(selectedWeek);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const changeWeek = (direction) => {
    const current = new Date(selectedWeek);
    current.setDate(current.getDate() + (direction * 7));
    setSelectedWeek(current.toISOString().split('T')[0]);
  };

  const getISOWeekNumber = (dateStr) => {
    const date = new Date(dateStr);
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const navItems = [
    { id: 'shifts', icon: Calendar, label: 'Shifts' },
    { id: 'requests', icon: CheckCircle, label: 'Requests', badge: shiftRequests.length },
    { id: 'reports', icon: BarChart, label: 'Reports' },
    { id: 'notifications', icon: Bell, label: 'Notifications', badge: unreadCount },
    { id: 'unavailability', icon: XCircle, label: 'Unavailability' },
    { id: 'attendance', icon: Fingerprint, label: 'Attendance' },
  ];

  // Shared context for child components
  const ctx = {
    user, isOwnBranch, shifts, shiftRequests, notifications, unavailability,
    weeklyReport, selectedWeek, selectedRoles, setSelectedRoles,
    managerOnDuty, branchStaff, staffList, systemRoles, shiftTemplates, branches,
    selectedStaffIds, setSelectedStaffIds, overrideHoursLimit, setOverrideHoursLimit,
    showCreateShift, setShowCreateShift, showEditShift, setShowEditShift,
    showAssignShift, setShowAssignShift, showUnavailability, setShowUnavailability,
    showCreateDirectShift, setShowCreateDirectShift,
    editingShift, setEditingShift, assigningShift,
    shiftForm, setShiftForm, directShiftForm, setDirectShiftForm,
    handleCreateShifts, handleCreateDirectShifts, handleShiftRequest,
    handleExportShiftsPDF, handleEditShift, handleSaveShiftEdit,
    handleUnassignShift, handleUnassignUser, openAssignDialog, handleAssignShiftMultiple,
    toggleStaffSelection, handleAddUnavailability, handleDeleteUnavailability,
    getWeekDates,
    templateWeeks, showApplyTemplateWeek, setShowApplyTemplateWeek,
    handleApplyTemplateWeek, selectedBranch, setSelectedBranch, changeWeek,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      {/* ───── Sidebar ───── */}
      <div className={`${sidebarCollapsed ? 'w-16' : 'w-48'} bg-gray-800 text-white transition-all duration-300 flex flex-col fixed h-full z-20`}>
        <div className={`p-3 border-b border-gray-700 flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
          <img
            src={sidebarCollapsed
              ? '/tmc-icon.png'
              : 'https://www.themerchantcoffee.co.za/merchant%20white%20logo_No%20Strap%20-1-.png'}
            alt="The Merchant Coffee"
            className={`${sidebarCollapsed ? 'h-8 w-8 object-contain bg-white rounded-lg p-1' : 'h-9 w-auto'}`}
          />
          {!sidebarCollapsed ? (
            <button onClick={() => setSidebarCollapsed(true)} className="p-1 hover:bg-gray-700 rounded" data-testid="collapse-sidebar-btn">
              <ChevronLeft className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={() => setSidebarCollapsed(false)} className="p-1 hover:bg-gray-700 rounded ml-1" data-testid="expand-sidebar-btn">
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-2">
            {navItems.map(item => (
              <li key={item.id} className="relative">
                <button
                  onClick={() => setActiveTab(item.id)}
                  data-testid={`tab-${item.id}`}
                  title={sidebarCollapsed ? item.label : ''}
                  className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : ''} gap-3 px-3 py-2.5 rounded-lg transition ${
                    activeTab === item.id ? 'bg-gray-600 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {!sidebarCollapsed && (
                    <>
                      <span className="flex-1 text-left text-sm">{item.label}</span>
                      {item.badge > 0 && <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{item.badge}</span>}
                    </>
                  )}
                  {sidebarCollapsed && item.badge > 0 && (
                    <span className="absolute left-12 top-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">{item.badge}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-gray-700">
          {!sidebarCollapsed && (
            <div className="mb-3 text-sm">
              <div className="text-white font-medium truncate">{user.username}</div>
              <div className="text-gray-400 text-xs capitalize">{user.role}</div>
            </div>
          )}
          <Button onClick={logout} variant="ghost" data-testid="logout-button"
            className={`w-full flex items-center gap-2 text-gray-300 hover:text-white hover:bg-gray-700 ${sidebarCollapsed ? 'justify-center px-2' : ''}`} size="sm">
            <LogOut className="w-4 h-4" />
            {!sidebarCollapsed && <span>Logout</span>}
          </Button>
        </div>
      </div>

      {/* ───── Main Content Area ───── */}
      <div className={`flex-1 ${sidebarCollapsed ? 'ml-16' : 'ml-48'} transition-all duration-300`}>
        {/* Content */}
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsContent value="shifts"><ManagerShiftsTab ctx={ctx} /></TabsContent>
            <TabsContent value="requests"><ManagerRequestsTab ctx={ctx} /></TabsContent>
            <TabsContent value="reports"><ManagerReportsTab ctx={ctx} /></TabsContent>
            <TabsContent value="notifications"><ManagerNotificationsTab ctx={ctx} /></TabsContent>
            <TabsContent value="unavailability"><ManagerUnavailabilityTab ctx={ctx} /></TabsContent>
            <TabsContent value="attendance">
              <div className="bg-white rounded-xl shadow-md border border-stone-200 p-4 sm:p-6">
                <div className="mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-stone-800 flex items-center gap-2">
                    <Clock className="w-5 h-5" /> Team Attendance
                  </h2>
                  <p className="text-xs sm:text-sm text-stone-600 mt-1">Sign off team attendance records or request edits on behalf of staff.</p>
                </div>
                <AttendanceView mode="manager" teamUsers={branchStaff} />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Dialogs */}
      <CreateShiftDialog ctx={ctx} />
      <AssignShiftDialog ctx={ctx} />
      <EditShiftDialog ctx={ctx} />
      <UnavailabilityDialog ctx={ctx} />
      <DirectCreateShiftDialog ctx={ctx} />
      <ApplyTemplateWeekDialog ctx={ctx} />
    </div>
  );
}
